import { Box as ChakraBox, BoxProps as ChakraBoxProps } from "@chakra-ui/react";
import { forwardRef, type CSSProperties } from "react";
import type { BoxSharedProps } from "./Box.props";

export type BoxProps = BoxSharedProps & {
  // Add any web specific props here.
  as?: ChakraBoxProps["as"]; // couldnt figure out how to recreate this type
};

// Check if a value is an Emotion SerializedStyles object (has 'styles' and 'name' properties)
function isEmotionStyles(value: unknown): boolean {
  return (
    value !== null &&
    typeof value === "object" &&
    "styles" in value &&
    "name" in value
  );
}

// Process css/sx values - convert Emotion SerializedStyles to raw CSS string wrapped in &
function processStyleValue(value: unknown): unknown {
  if (!value) return value;

  if (isEmotionStyles(value)) {
    // Extract raw CSS from Emotion SerializedStyles and wrap for Chakra
    const { styles } = value as { styles: string };
    return { "&": styles };
  }

  if (Array.isArray(value)) {
    return value.map(processStyleValue);
  }

  return value;
}

const Box = forwardRef<HTMLDivElement, BoxProps>(function Box(
  props,
  ref
): JSX.Element {
  const {
    as = "div",
    aspectRatio,
    sx,
    css,
    style,
    d,
    display,
    ...rest
  } = props;

  const mergedDisplay = d || display;

  // Process css/sx props to handle Emotion SerializedStyles
  const processedCss = processStyleValue(css);
  const processedSx = processStyleValue(sx);

  // Merge sx (v1/v2 legacy) and css props
  const mergedCss =
    processedSx && processedCss
      ? ([processedCss, processedSx] as unknown as ChakraBoxProps["css"])
      : processedSx ?? processedCss;

  const mergedStyle: CSSProperties | undefined =
    aspectRatio !== undefined ? { ...style, aspectRatio } : style;

  return (
    <ChakraBox
      ref={ref}
      as={as}
      css={mergedCss as ChakraBoxProps["css"]}
      style={mergedStyle}
      display={mergedDisplay}
      {...(rest as unknown as ChakraBoxProps)}
    />
  );
});

export default Box;
